import { useCallback, useEffect, useState } from 'react'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import { fetchEquityTieOut, getCapitalAccounts } from '@/services/waterfall'
import type { CapitalAccountMember, EquityTieOutResponse } from '@/types/waterfall'
import { MEMBER_LABELS, MEMBERS } from '@/types/waterfall'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import { allocationAmountClassName } from '@/utils/allocationFormat'
import { todayIsoDate } from '@/utils/waterfallCalculator'
import { equityTieOutNeedsWarning } from './equityTieOut'
import WaterfallTabHeader from './WaterfallTabHeader'

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

export default function CapitalAccounts() {
  const [asof, setAsof] = useState(todayIsoDate())
  const [members, setMembers] = useState<CapitalAccountMember[]>([])
  const [methodNote, setMethodNote] = useState('')
  const [totalEquity, setTotalEquity] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tieOut, setTieOut] = useState<EquityTieOutResponse | null>(null)
  const [tieOutLoading, setTieOutLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getCapitalAccounts(asof)
      setMembers(data.members)
      setMethodNote(data.method_note)
      setTotalEquity(Number(data.total_equity))
    } catch (e) {
      setError(formatApiError(e, 'Failed to load capital accounts'))
      setMembers([])
      setTotalEquity(null)
    } finally {
      setLoading(false)
    }
  }, [asof])

  useEffect(() => {
    void load()
  }, [load])

  const runTieOut = async () => {
    setTieOutLoading(true)
    try {
      const result = await fetchEquityTieOut()
      setTieOut(result)
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to tie out equity to QuickBooks'))
    } finally {
      setTieOutLoading(false)
    }
  }

  const membersByCode = Object.fromEntries(members.map((m) => [m.member, m])) as Record<
    string,
    CapitalAccountMember | undefined
  >

  return (
    <div className="w-full space-y-6">
      <WaterfallTabHeader
        title="Accruals"
        description="Member capital account roll-forward under the targeted capital account method."
      />

      <div className="flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">As of</span>
          <input
            type="date"
            value={asof}
            onChange={(e) => setAsof(e.target.value)}
            className="mt-1 block rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
          />
        </label>
        {totalEquity != null && (
          <p className="text-sm text-gray-600">
            Total book equity:{' '}
            <span className="font-medium text-gray-900">{fmt.format(totalEquity)}</span>
          </p>
        )}
        <Button type="button" gradient disabled={tieOutLoading} onClick={() => void runTieOut()}>
          {tieOutLoading ? 'Tying out…' : 'Tie out to QuickBooks'}
        </Button>
      </div>

      {tieOut && (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-gray-700">
              Equity tie-out as of <span className="font-medium">{tieOut.as_of}</span>
            </p>
            {equityTieOutNeedsWarning(tieOut.difference) && (
              <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                Out of balance
              </span>
            )}
          </div>
          <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-gray-500">Engine total equity</dt>
              <dd className="font-medium tabular-nums">{fmt.format(tieOut.engine_total_equity)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">QBO total equity</dt>
              <dd className="font-medium tabular-nums">{fmt.format(tieOut.qbo_total_equity)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Difference</dt>
              <dd
                className={`font-medium tabular-nums ${allocationAmountClassName(tieOut.difference)}`}
              >
                {fmt.format(tieOut.difference)}
              </dd>
            </div>
          </dl>
        </div>
      )}

      {methodNote && (
        <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          {methodNote}
        </p>
      )}

      {loading && <p className="text-sm text-gray-500">Loading capital accounts…</p>}

      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {!loading && !error && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-600">Member</th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">Join</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Cum contrib</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Cum transfers</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Cum dist</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Cum alloc</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Current</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Target</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Drift</th>
              </tr>
            </thead>
            <tbody>
              {MEMBERS.map((code) => {
                const row = membersByCode[code]
                const current = Number(row?.current_capital ?? 0)
                const negativeCapital = current < -0.005
                return (
                  <tr key={code} className="border-t border-gray-100">
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{code}</span>
                        <span className="text-xs text-gray-500">{MEMBER_LABELS[code]}</span>
                        {negativeCapital && (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800">
                            Negative capital
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{row?.join_date ?? '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {fmt.format(Number(row?.cumulative_contributions ?? 0))}
                    </td>
                    <td
                      className={`px-4 py-2 text-right tabular-nums ${allocationAmountClassName(Number(row?.cumulative_transfers ?? 0))}`}
                    >
                      {fmt.format(Number(row?.cumulative_transfers ?? 0))}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {fmt.format(Number(row?.cumulative_distributions ?? 0))}
                    </td>
                    <td
                      className={`px-4 py-2 text-right tabular-nums ${allocationAmountClassName(Number(row?.cumulative_allocations ?? 0))}`}
                    >
                      {fmt.format(Number(row?.cumulative_allocations ?? 0))}
                    </td>
                    <td
                      className={`px-4 py-2 text-right tabular-nums font-medium ${allocationAmountClassName(current)}`}
                    >
                      {fmt.format(current)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                      {fmt.format(Number(row?.current_target ?? 0))}
                    </td>
                    <td
                      className={`px-4 py-2 text-right tabular-nums ${allocationAmountClassName(Number(row?.drift ?? 0))}`}
                    >
                      {fmt.format(Number(row?.drift ?? 0))}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
